import { RefObject, useState } from 'react';

import { ModalData, ChartCardModal } from '@backrs_ui/common-ui/src/atoms/Charts/ChartCardModal';
import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Typography, SxProps, Theme, Tooltip, ClickAwayListener } from '@mui/material';

export type SegmentedBarRowData = Readonly<{
  title: string;
  options: readonly string[];
  selectedValue: string;
  infoVariant?: { type: 'tooltip'; text: string } | { type: 'modal'; modalData: ModalData };
}>;

type SegmentedBarCardProps = Readonly<{
  title: string;
  rows: readonly SegmentedBarRowData[];
  containerRef?: RefObject<HTMLDivElement | null>;
}>;

export const SegmentedBarCard = ({ title, rows, containerRef }: SegmentedBarCardProps) => {
  return (
    <Box ref={containerRef} sx={cardContainerSx}>
      <Box sx={titleSx}>
        <Typography variant="text-sm-semibold">{title}</Typography>
      </Box>
      <Box sx={rowContainerWrapperSx}>
        {rows.map((row) => (
          <SegmentedBarRow key={row.title} row={row} />
        ))}
      </Box>
    </Box>
  );
};

type SegmentedBarRowProps = Readonly<{
  row: SegmentedBarRowData;
}>;

const SegmentedBarRow = ({ row }: SegmentedBarRowProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedIndex = row.options.findIndex(
    (option) => option.toLowerCase() === String(row.selectedValue).toLowerCase(),
  );

  return (
    <Box
      sx={rowContainerSx}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box sx={rowHeaderSx}>
        <Typography variant="text-sm-medium" sx={rowTitleSx}>
          {row.title}
        </Typography>
        {row.infoVariant?.type === 'tooltip' && (
          <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
            <Tooltip
              open={isTooltipOpen}
              onClose={() => setIsTooltipOpen(false)}
              title={row.infoVariant.text}
              placement="top"
              arrow
              disableFocusListener
              disableHoverListener
              disableTouchListener
              slotProps={{
                tooltip: {
                  sx: {
                    whiteSpace: 'pre-line',
                    padding: '16px',
                  },
                },
              }}
            >
              <Box
                id={`segmented-bar-info-${row.title.toLowerCase().replace(/\s+/g, '-')}`}
                sx={tooltipIconSx}
                onClick={() => setIsTooltipOpen((prev) => !prev)}
              >
                <AppIcon iconName="InfoCircle" diameter="14px" color="Colors/Text/text-tertiary" />
              </Box>
            </Tooltip>
          </ClickAwayListener>
        )}
        {row.infoVariant?.type === 'modal' && (
          <>
            <Box
              id={`segmented-bar-info-${row.title.toLowerCase().replace(/\s+/g, '-')}`}
              sx={tooltipIconSx}
              onClick={() => setIsModalOpen(true)}
            >
              <AppIcon iconName="InfoCircle" diameter="14px" color="Colors/Text/text-tertiary" />
            </Box>
            <ChartCardModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              modalData={row.infoVariant.modalData}
            />
          </>
        )}
      </Box>

      <Box sx={segmentedBarContainerSx(isHovered)}>
        {row.options.map((option, index) => {
          const isSelected = index === selectedIndex;
          const segmentColor: ColorToken = isSelected ? 'Brand/500' : 'Brand/50';
          const shouldShowLabel = isHovered || isSelected;
          const isFirst = index === 0;
          const isLast = index === row.options.length - 1;

          return (
            <Box key={option} sx={segmentSx(segmentColor, isFirst, isLast)}>
              <Typography
                variant="text-xs-medium"
                sx={segmentLabelSx(isSelected, isHovered, shouldShowLabel)}
              >
                {option}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const cardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary-hover']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  flex: 1,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-2xl'],
  py: backrs.spacing['spacing-lg'],
});

const rowContainerWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  px: backrs.spacing['spacing-xl'],
  py: backrs.spacing['spacing-2xl'],
});

const rowContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '60px',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'center',
    height: 'inherit',
  },
};

const rowHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  minWidth: 'auto',
  flexShrink: 0,
  justifyContent: 'flex-start',
  pr: 0,
  pb: backrs.spacing['spacing-sm'],
  width: '100%',
  '@container (min-width: 600px)': {
    minWidth: '160px',
    justifyContent: 'flex-end',
    pr: backrs.spacing['spacing-2xl'],
    pb: 0,
    width: 'auto',
  },
});

const rowTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const tooltipIconSx: SxProps<Theme> = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const segmentedBarContainerSx =
  (isHovered: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flex: 1,
    height: '35px',
    ...(isHovered && {
      boxShadow: backrs.shadows['shadow-lg'],
      borderRadius: backrs.radius['radius-xs'],
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    }),
    transition: 'all 0.3s ease-in-out',
  });

const segmentSx =
  (color: ColorToken, isFirst: boolean, isLast: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: backrs.colors[color],
    borderTopRightRadius: isLast ? backrs.radius['radius-xs'] : 0,
    borderBottomRightRadius: isLast ? backrs.radius['radius-xs'] : 0,
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    borderLeft: isFirst ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
  });

const segmentLabelSx =
  (isSelected: boolean, isHovered: boolean, shouldShowLabel: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: isSelected
      ? backrs.colors['Colors/Base/White']
      : isHovered
        ? backrs.colors['Colors/Text/text-quaternary']
        : 'transparent',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: shouldShowLabel ? 1 : 0,
    transition: 'all 0.3s ease-in-out',
  });
